'use client';

import Image from 'next/image';
import { useEffect, useId, useRef, useState } from 'react';
import { AppleLogo, ArrowUpRight, GooglePlayLogo } from '@phosphor-icons/react';
import { BagIcon, HeartIcon } from '@/components/icons';
import './about-app-scene.css';

// The same destinations are used by SmartAppBanner and MobileAppBanner.
const APP_STORE_URL = 'https://apps.apple.com/ru/app/азиаточка-доставка-еды/id6761508832';
const GOOGLE_PLAY_URL = 'https://play.google.com/store/apps/details?id=com.steepdesign.aziaapp';

export function AboutAppScene() {
  const id = useId();
  const sceneRef = useRef<HTMLDivElement>(null);
  const [hasEntered, setHasEntered] = useState(false);

  useEffect(() => {
    const element = sceneRef.current;
    if (
      !element ||
      !('IntersectionObserver' in window) ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    )
      return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setHasEntered(true);
        observer.disconnect();
      },
      { threshold: 0.3 }
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="ab-app" aria-labelledby={`${id}-title`}>
      <div className="ab-app-copy">
        <span className="ab-app-eyebrow">Приложение Азиаточка</span>
        <h2 id={`${id}-title`}>
          Роллы ближе,
          <br />
          чем кажется.
        </h2>
        <p>
          Любимые блюда, бонусы и приятные предложения. Всё, за что вы любите Азиаточку, — теперь
          под рукой.
        </p>
        <div className="ab-app-downloads" aria-label="Скачать приложение Азиаточка">
          <a
            href={APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Скачать Азиаточку в App Store, откроется в новой вкладке"
          >
            <AppleLogo size={28} weight="fill" aria-hidden="true" />
            <span>
              <small>Скачать в</small>
              <strong>App Store</strong>
            </span>
            <ArrowUpRight className="ab-app-store-arrow" size={17} aria-hidden="true" />
          </a>
          <a
            href={GOOGLE_PLAY_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Скачать Азиаточку в Google Play, откроется в новой вкладке"
          >
            <GooglePlayLogo size={26} weight="fill" aria-hidden="true" />
            <span>
              <small>Скачать в</small>
              <strong>Google Play</strong>
            </span>
            <ArrowUpRight className="ab-app-store-arrow" size={17} aria-hidden="true" />
          </a>
        </div>
        <span className="ab-app-availability">Для iOS и Android</span>
      </div>

      <div className="ab-app-scene" ref={sceneRef} data-entered={hasEntered} aria-hidden="true">
        <div className="ab-app-ground" />
        <div className="ab-app-sculpture">
          <div className="ab-app-satellite ab-app-satellite-bag">
            <BagIcon size={49} weight="regular" />
          </div>
          <div className="ab-app-tile">
            <div className="ab-app-face">
              <Image src="/favicon.svg" alt="" width={300} height={300} sizes="300px" />
              <i />
            </div>
          </div>
          <div className="ab-app-satellite ab-app-satellite-heart">
            <HeartIcon size={35} weight="fill" />
          </div>
        </div>
        <div className="ab-app-scene-caption">
          <span />
          Всегда рядом
        </div>
      </div>
    </section>
  );
}
